import Header from "../../components/GSO/Header";
import { Box, Paper, Typography, Container, Grid, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";

const LostItems = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");

  // Table headers data
  const tableHeaders = [
    "Claimant Name",
    "Contact",
    "Item Description",
    "Linked Item",
    "Date Submitted",
    "Status",
    "Actions"
  ];
  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        setError("Please login to view claims");
        return;
      }
      
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl("api/claims/all"), {
        headers: getApiHeaders({
          "Authorization": `Bearer ${token}`
        })
      });

      if (response.status === 403) {
        setError("Access denied. You need GSO, CO-ADMIN, or ADMIN role to view claims.");
        setClaims([]);
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch claims");
      }

      const data = await response.json();
      console.log("Fetched claims data:", data.claims); // Debug log
      setClaims(data.claims || []);
    } catch (err) {
      setError(err.message || "Error fetching claims");
      console.error("Error fetching claims:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = (claim, status) => {
    setSelectedClaim(claim);
    setReviewStatus(status);
    setReviewNotes("");
    setReviewDialog(true);
  };

  const handleCloseReview = () => {
    setReviewDialog(false);
    setSelectedClaim(null);
    setReviewNotes("");
    setReviewStatus("");
  };

  const handleSubmitReview = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl(`api/claims/${selectedClaim.id}/status`), {
        method: "PUT",
        headers: getApiHeaders({
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }),
        body: JSON.stringify({
          status: reviewStatus,
          review_notes: reviewNotes
        })
      });

      if (!response.ok) {
        throw new Error("Failed to update claim status");
      }

      alert("Claim status updated successfully!");
      handleCloseReview();
      fetchClaims(); // Refresh the list
    } catch (err) {
      alert("Error updating claim: " + err.message);
      console.error("Error updating claim:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "warning";
      case "approved": return "success";
      case "rejected": return "error";
      case "claimed": return "info";
      default: return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#FBFFE9",
        padding: 0,
        margin: 0,
        overflow: "hidden",
        position: "relative",
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
      }}
    >
  {/* Header Component */}
  <Header title="Lost Item Reports" />

      {/* Table container with updated size */}
      <Container maxWidth="xl" sx={{ mt: 3, px: 6 }}>
        <Paper elevation={4} sx={{ bgcolor: "#E1ECC8", p: 4, borderRadius: "30px" }}>
          <Grid container>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "#FBFFE9",
                  borderRadius: "15px",
                  p: 3,
                  overflow: "hidden",
                  minHeight: "70vh",
                }}
              >
                {/* Table headers and cells */}
                <Box
                  sx={{
                    width: "100%",
                    bgcolor: "#FBFFE9",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid #d0e7ba",
                  }}
                >
                  {/* Header row */}
                  <Box
                    sx={{
                      display: "flex",
                      borderBottom: "1px solid #d0e7ba",
                      bgcolor: "#FBFFE9",
                    }}
                  >
                    {tableHeaders.map((header, index) => (
                      <Box
                        key={index}
                        sx={{
                          flex: 1,
                          p: 2,
                          textAlign: "center",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 500,
                          fontSize: "1rem",
                          color: "#526D50",
                          borderRight: index < tableHeaders.length - 1 ? "1px solid #d0e7ba" : "none",
                        }}
                      >
                        {header}
                      </Box>
                    ))}
                  </Box>

                  {/* Claims data rows */}
                  <Box>
                    {loading ? (
                      <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography sx={{ color: "#526D50" }}>Loading claims...</Typography>
                      </Box>
                    ) : error ? (
                      <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography sx={{ color: "#c74f4f" }}>{error}</Typography>
                      </Box>
                    ) : claims.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography sx={{ color: "#526D50" }}>No claims submitted yet</Typography>
                      </Box>
                    ) : (
                      claims.map((claim, index) => (
                        <Box
                          key={claim.id}
                          sx={{
                            display: "flex",
                            borderBottom: index < claims.length - 1 ? "1px solid #d0e7ba" : "none",
                            minHeight: "80px",
                            alignItems: "center",
                          }}
                        >
                          {/* Claimant Name */}
                          <Box sx={{ flex: 1, p: 2, borderRight: "1px solid #d0e7ba" }}>
                            <Typography sx={{ fontSize: "0.9rem", color: "#526D50", fontWeight: 500 }}>
                              {claim.full_name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#7a927a" }}>
                              {claim.email}
                            </Typography>
                          </Box>
                          
                          {/* Contact */}
                          <Box sx={{ flex: 1, p: 2, borderRight: "1px solid #d0e7ba" }}>
                            <Typography sx={{ fontSize: "0.9rem", color: "#526D50" }}>
                              {claim.contact_number}
                            </Typography>
                          </Box>
                          
                          {/* Item Description */}
                          <Box sx={{ flex: 1, p: 2, borderRight: "1px solid #d0e7ba" }}>
                            <Typography sx={{ fontSize: "0.85rem", color: "#526D50", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                              {claim.item_description}
                            </Typography>
                          </Box>
                          
                          {/* Linked Item */}
                          <Box sx={{ flex: 1, p: 2, borderRight: "1px solid #d0e7ba" }}>
                            {claim.found_item_id ? (
                              <Box>
                                <Typography sx={{ fontSize: "0.85rem", color: "#526D50", fontWeight: 500 }}>
                                  {claim.item_name || "Item #" + claim.found_item_id}
                                </Typography>
                                <Typography sx={{ fontSize: "0.75rem", color: "#7a927a" }}>
                                  {claim.item_type}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography sx={{ fontSize: "0.85rem", color: "#999", fontStyle: "italic" }}>
                                No item linked
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Date Submitted */}
                          <Box sx={{ flex: 1, p: 2, borderRight: "1px solid #d0e7ba" }}>
                            <Typography sx={{ fontSize: "0.85rem", color: "#526D50" }}>
                              {formatDate(claim.claim_date)}
                            </Typography>
                          </Box>
                          
                          {/* Status */}
                          <Box sx={{ flex: 1, p: 2, borderRight: "1px solid #d0e7ba", textAlign: "center" }}>
                            <Chip 
                              label={claim.status.toUpperCase()} 
                              color={getStatusColor(claim.status)}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                          
                          {/* Actions */}
                          <Box sx={{ flex: 1, p: 2, display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                            {claim.status === "pending" && (
                              <>
                                <Button 
                                  size="small" 
                                  variant="contained"
                                  onClick={() => handleOpenReview(claim, "approved")}
                                  sx={{ 
                                    bgcolor: "#4caf50", 
                                    "&:hover": { bgcolor: "#45a049" },
                                    fontSize: "0.75rem",
                                    px: 1.5,
                                    py: 0.5
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button 
                                  size="small" 
                                  variant="contained"
                                  onClick={() => handleOpenReview(claim, "rejected")}
                                  sx={{ 
                                    bgcolor: "#f44336", 
                                    "&:hover": { bgcolor: "#d32f2f" },
                                    fontSize: "0.75rem",
                                    px: 1.5,
                                    py: 0.5
                                  }}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {claim.status === "approved" && (
                              <Button 
                                size="small" 
                                variant="contained"
                                onClick={() => handleOpenReview(claim, "claimed")}
                                sx={{ 
                                  bgcolor: "#2196f3", 
                                  "&:hover": { bgcolor: "#1976d2" },
                                  fontSize: "0.75rem",
                                  px: 1.5,
                                  py: 0.5
                                }}
                              >
                                Mark Claimed
                              </Button>
                            )}
                            {claim.status !== "pending" && (
                              <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  alert(`Claim Details:\n\nClaimant: ${claim.full_name}\nEmail: ${claim.email}\nPhone: ${claim.contact_number}\n\nDescription:\n${claim.item_description}\n\nNotes:\n${claim.additional_notes || "None"}\n\nReview Notes:\n${claim.review_notes || "None"}`);
                                }}
                                sx={{ 
                                  borderColor: "#526D50",
                                  color: "#526D50",
                                  fontSize: "0.75rem",
                                  px: 1.5,
                                  py: 0.5
                                }}
                              >
                                View
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={handleCloseReview} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#E1ECC8", color: "#526D50", fontWeight: 600 }}>
          {reviewStatus === "approved" ? "Approve Claim" : reviewStatus === "rejected" ? "Reject Claim" : "Mark as Claimed"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedClaim && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#526D50", fontWeight: 600, mb: 1 }}>
                Claimant: {selectedClaim.full_name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#7a927a", mb: 2 }}>
                {selectedClaim.item_description}
              </Typography>
            </Box>
          )}
          <TextField
            label="Review Notes (Optional)"
            multiline
            rows={4}
            fullWidth
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Add any notes about this decision..."
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#526D50',
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseReview} sx={{ color: "#7a927a" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitReview} 
            variant="contained"
            sx={{ 
              bgcolor: reviewStatus === "rejected" ? "#f44336" : "#4caf50",
              "&:hover": { 
                bgcolor: reviewStatus === "rejected" ? "#d32f2f" : "#45a049"
              }
            }}
          >
            Confirm {reviewStatus === "approved" ? "Approval" : reviewStatus === "rejected" ? "Rejection" : "Claimed"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LostItems;